import { Store } from 'redux';
import { GarbageCollector } from 'utils/garbage-collector';
import { RootState } from '../stores/reducers';

export interface LoadDataCallbackParams {
  getState: Store['getState'];
  dispatch: Store['dispatch'];
  subscribe: (callback: (state: RootState) => unknown) => void;
  collect: (cleanup: () => unknown) => unknown;
  resolve: <T = unknown>(value?: T) => void;
  reject: <R = unknown>(reason?: R) => void;
}

export type LoadDataCallback<T> = (
  params: LoadDataCallbackParams,
  props?: T
) => void | Promise<void | unknown>;

export const createLoadData = <T extends unknown>(cb: LoadDataCallback<T>) => (store: Store, props?: T) => {
  const collector = new GarbageCollector();

  function subscribe(callback: (state: RootState) => void) {
    let currentState: RootState;

    function handleChange() {
      let nextState = store.getState();

      if (nextState !== currentState) {
        currentState = nextState;
        callback(currentState);
      }
    }

    collector.add(store.subscribe(handleChange));
    handleChange();
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => reject(new Error('timeout')), process.env.NODE_ENV === 'test' ? 1000 : 60000);

    const result = cb(
      {
        getState: store.getState,
        dispatch: store.dispatch,
        collect: collector.add,
        subscribe,
        resolve,
        reject,
      },
      props
    );

    if (result instanceof Promise) {
      result.then(resolve).catch(reject);
    }
  }).finally(collector.cleanup);
};
